import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/hkApi";
import { toast } from "sonner";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { BarChart3, Clock, CheckCircle2, ClipboardList, TrendingUp, Loader2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";

const RANGES = [
  { key: 7,  label: "7 giorni" },
  { key: 30, label: "30 giorni" },
  { key: 90, label: "90 giorni" },
];

export default function Stats() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stats", { params: { days } });
      setData(data);
    } catch (e) {
      toast.error("Errore caricamento statistiche");
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <div className="max-w-2xl mx-auto -mt-6 py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4A1C17]" /></div>;
  }

  const t = data.totals;
  const chartDays = data.days.map((d) => ({
    ...d,
    label: format(parseISO(d.date), "d MMM", { locale: it }),
  }));

  return (
    <div data-testid="hk-stats-page" className="max-w-2xl mx-auto px-4 -mt-6 space-y-4">
      <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-md p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <BarChart3 className="h-5 w-5 text-[#4A1C17]" />
          <h2 className="font-display text-lg font-bold text-[#2B1410] flex-1">Statistiche pulizie</h2>
          <div className="inline-flex rounded-full bg-[#FAF3EE] p-1 border border-[#E8DCC8]">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setDays(r.key)}
                data-testid={`stats-range-${r.key}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold ${days === r.key ? "bg-[#4A1C17] text-white" : "text-[#4A1C17]"}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs text-[#7C5A4E] mt-1">
          Dal {format(parseISO(data.from), "d MMM", { locale: it })} al {format(parseISO(data.to), "d MMM yyyy", { locale: it })}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Kpi Icon={CheckCircle2} color="#137333" label="Camere completate" value={t.rooms_completed} />
        <Kpi Icon={ClipboardList} color="#4A1C17" label="Task eseguiti" value={t.tasks_done} />
        <Kpi Icon={Clock} color="#C5A059" label="Tempo medio riassetto" value={t.avg_time_min != null ? `${t.avg_time_min} min` : "—"} />
        <Kpi Icon={TrendingUp} color="#7C5A4E" label="Giorni attivi" value={`${t.active_days}/${data.days.length}`} />
      </div>

      <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-sm p-3 sm:p-4" data-testid="stats-chart-completed">
        <h3 className="text-sm font-semibold text-[#2B1410] mb-2">Camere completate al giorno</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartDays} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8DCC8" />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#7C5A4E" }} interval="preserveStartEnd" />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#7C5A4E" }} />
            <Tooltip
              contentStyle={{ background: "#4A1C17", border: "none", borderRadius: 8, color: "#F7EBDF", fontSize: 12 }}
              labelStyle={{ color: "#C5A059" }}
              formatter={(v) => [v, "Camere"]}
            />
            <Bar dataKey="rooms_completed" fill="#4A1C17" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-sm p-3 sm:p-4" data-testid="stats-chart-time">
        <h3 className="text-sm font-semibold text-[#2B1410] mb-2">Tempo medio riassetto (min)</h3>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartDays} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8DCC8" />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#7C5A4E" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fill: "#7C5A4E" }} />
            <Tooltip
              contentStyle={{ background: "#4A1C17", border: "none", borderRadius: 8, color: "#F7EBDF", fontSize: 12 }}
              labelStyle={{ color: "#C5A059" }}
              formatter={(v) => [v != null ? `${v} min` : "—", "Tempo"]}
            />
            <Line type="monotone" dataKey="avg_time_min" stroke="#C5A059" strokeWidth={2.5} dot={{ r: 3, fill: "#C5A059" }} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-sm p-3 sm:p-4" data-testid="stats-by-room">
        <h3 className="text-sm font-semibold text-[#2B1410] mb-2">Per camera</h3>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-[#7C5A4E]">
            <tr>
              <th className="text-left py-1.5">Camera</th>
              <th className="text-center py-1.5">Complete</th>
              <th className="text-center py-1.5">Task</th>
              <th className="text-right py-1.5">Tempo medio</th>
            </tr>
          </thead>
          <tbody>
            {data.by_room.map((r) => (
              <tr key={r.room_id} className="border-t border-[#E8DCC8]" data-testid={`stats-room-${r.room_id}`}>
                <td className="py-1.5 font-semibold text-[#2B1410]">{r.number}. {r.room_name}</td>
                <td className="py-1.5 text-center font-mono">{r.rooms_completed}</td>
                <td className="py-1.5 text-center font-mono">{r.tasks_done}</td>
                <td className="py-1.5 text-right font-mono">{r.avg_time_min != null ? `${r.avg_time_min} min` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ Icon, color, label, value }) {
  return (
    <div className="rounded-2xl border border-[#E8DCC8] bg-white shadow-sm px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-[#7C5A4E]">
        <Icon className="h-3 w-3" style={{ color }} /> {label}
      </div>
      <div className="text-xl font-bold text-[#2B1410] mt-0.5">{value}</div>
    </div>
  );
}
